import {
  ContentType,
  Role,
  type RemovalBehavior,
  type UpdateFrequency,
} from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Dialog, toast } from '@utima/ui';
import { memo, useEffect, useMemo, useState } from 'react';

import { AutoUpdateSettingsForm } from '@/features/documents/components/AutoUpdateSettingsForm';
import { useAccess } from '@/hooks/useAccess';

import type { KBContentRow } from './kbTypes';
import type { useKBMutations } from '../hooks/useKBMutations';
import { isManagedSource, isWebsiteDiscoveryRoot } from '../lib/managedSource';

type BulkAutoUpdateModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedRows: KBContentRow[];
  mutations: ReturnType<typeof useKBMutations>;
};

type AutoUpdateSnapshot = {
  updateFrequency: UpdateFrequency | null;
  discoveryFrequency?: UpdateFrequency | null;
  changeThreshold: number | null;
  autoIncludeNewPages?: boolean | null;
  removalBehavior?: RemovalBehavior | null;
};

function getUniformValue<T>(values: T[]): T | undefined {
  if (values.length === 0) {
    return undefined;
  }

  const first = values[0];

  return values.every(value => value === first) ? first : undefined;
}

export const BulkAutoUpdateModal = memo(function BulkAutoUpdateModal({
  open,
  onOpenChange,
  selectedRows,
  mutations,
}: BulkAutoUpdateModalProps) {
  const { t } = useLingui();
  const { hasAccess } = useAccess();
  const isSuperAdmin = hasAccess([Role.SuperAdmin]);
  const [updateFrequency, setUpdateFrequency] = useState<
    UpdateFrequency | null | undefined
  >(undefined);
  const [discoveryFrequency, setDiscoveryFrequency] = useState<
    UpdateFrequency | null | undefined
  >(undefined);
  const [changeThreshold, setChangeThreshold] = useState<
    number | null | undefined
  >(undefined);
  const [autoIncludeNewPages, setAutoIncludeNewPages] = useState<
    boolean | null | undefined
  >(undefined);
  const [removalBehavior, setRemovalBehavior] = useState<
    RemovalBehavior | null | undefined
  >(undefined);

  const documentRows = useMemo(
    () => selectedRows.filter(row => row.rowType === 'document'),
    [selectedRows],
  );
  const updatableDocumentRows = useMemo(
    () => documentRows.filter(row => row.data.type !== ContentType.Document),
    [documentRows],
  );
  const folderRows = useMemo(
    () => selectedRows.filter(row => row.rowType === 'folder'),
    [selectedRows],
  );
  const skippedManualDocumentsCount =
    documentRows.length - updatableDocumentRows.length;

  const documentIds = useMemo(
    () => updatableDocumentRows.map(row => row.id),
    [updatableDocumentRows],
  );
  const folderIds = useMemo(() => folderRows.map(row => row.id), [folderRows]);

  const hasWebsiteManagedFolder = useMemo(
    () =>
      folderRows.some(row => {
        const data = row.data as Record<string, unknown>;

        return !!data.sourceUri || !!data.websitePathPrefix;
      }),
    [folderRows],
  );

  // discoveryFrequency is root-only (not inherited by subfolders) — only a
  // selection that includes a website root can bulk-edit it.
  const hasWebsiteRootFolder = useMemo(
    () => folderRows.some(row => isWebsiteDiscoveryRoot(row.data)),
    [folderRows],
  );

  const snapshots = useMemo(() => {
    const docs: AutoUpdateSnapshot[] = documentRows.map(row => {
      const data = row.data as Record<string, unknown>;

      return {
        updateFrequency:
          (data.updateFrequency as UpdateFrequency | null) ?? null,
        changeThreshold:
          typeof data.changeThreshold === 'number'
            ? (data.changeThreshold as number)
            : null,
      };
    });

    const folders: AutoUpdateSnapshot[] = folderRows.map(row => {
      const data = row.data as Record<string, unknown>;
      const isManaged = isManagedSource(data);
      const isWebsiteRoot = isWebsiteDiscoveryRoot(data);

      return {
        updateFrequency:
          (data.updateFrequency as UpdateFrequency | null) ?? null,
        discoveryFrequency: isWebsiteRoot
          ? ((data.discoveryFrequency as UpdateFrequency | null) ?? null)
          : undefined,
        changeThreshold:
          typeof data.changeThreshold === 'number'
            ? (data.changeThreshold as number)
            : null,
        autoIncludeNewPages: isManaged
          ? ((data.autoIncludeNewPages as boolean | null) ?? null)
          : undefined,
        removalBehavior: isManaged
          ? ((data.removalBehavior as RemovalBehavior | null) ?? null)
          : undefined,
      };
    });

    return { docs, folders };
  }, [documentRows, folderRows]);

  useEffect(() => {
    if (!open) {
      setUpdateFrequency(undefined);
      setDiscoveryFrequency(undefined);
      setChangeThreshold(undefined);
      setAutoIncludeNewPages(undefined);
      setRemovalBehavior(undefined);

      return;
    }

    const combined = [...snapshots.docs, ...snapshots.folders];

    setUpdateFrequency(
      getUniformValue(combined.map(item => item.updateFrequency)),
    );
    setChangeThreshold(
      getUniformValue(combined.map(item => item.changeThreshold)),
    );

    if (hasWebsiteManagedFolder) {
      const websiteSnapshots = snapshots.folders.filter(
        item =>
          item.autoIncludeNewPages !== undefined &&
          item.removalBehavior !== undefined,
      );
      setAutoIncludeNewPages(
        getUniformValue(
          websiteSnapshots.map(item => item.autoIncludeNewPages ?? null),
        ),
      );
      setRemovalBehavior(
        getUniformValue(
          websiteSnapshots.map(item => item.removalBehavior ?? null),
        ),
      );
    } else {
      setAutoIncludeNewPages(undefined);
      setRemovalBehavior(undefined);
    }

    if (hasWebsiteRootFolder) {
      const websiteRootSnapshots = snapshots.folders.filter(
        item => item.discoveryFrequency !== undefined,
      );
      setDiscoveryFrequency(
        getUniformValue(
          websiteRootSnapshots.map(item => item.discoveryFrequency ?? null),
        ),
      );
    } else {
      setDiscoveryFrequency(undefined);
    }
  }, [open, hasWebsiteManagedFolder, hasWebsiteRootFolder, snapshots]);

  const handleSave = async () => {
    const effectiveChangeThreshold = isSuperAdmin ? changeThreshold : undefined;

    const hasDocumentPayload =
      documentIds.length > 0 &&
      (updateFrequency !== undefined || effectiveChangeThreshold !== undefined);

    const hasFolderPayload =
      folderIds.length > 0 &&
      (updateFrequency !== undefined ||
        discoveryFrequency !== undefined ||
        effectiveChangeThreshold !== undefined ||
        autoIncludeNewPages !== undefined ||
        removalBehavior !== undefined);

    if (!hasDocumentPayload && !hasFolderPayload) {
      toast.error(t`Choose at least one setting to update`);

      return;
    }

    try {
      await Promise.all([
        hasDocumentPayload
          ? mutations.bulkUpdateDocumentsAutoUpdate.mutateAsync({
              documentIds,
              updateFrequency,
              changeThreshold: effectiveChangeThreshold,
            })
          : Promise.resolve(),
        hasFolderPayload
          ? mutations.bulkUpdateFoldersAutoUpdate.mutateAsync({
              folderIds,
              updateFrequency,
              discoveryFrequency,
              changeThreshold: effectiveChangeThreshold,
              autoIncludeNewPages,
              removalBehavior,
            })
          : Promise.resolve(),
      ]);

      toast.success(t`Auto-update settings updated`);
      onOpenChange(false);
    } catch {
      toast.error(t`Failed to update auto-update settings`);
    }
  };

  const isSaving =
    mutations.bulkUpdateDocumentsAutoUpdate.isPending ||
    mutations.bulkUpdateFoldersAutoUpdate.isPending;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className='w-full max-w-[680px]'>
        <Dialog.Header>
          <Dialog.Title>
            <Trans>Auto-update settings</Trans>
          </Dialog.Title>
          <Dialog.Description>
            <Trans>
              Apply auto-update settings to all selected documents and folders.
            </Trans>
          </Dialog.Description>
        </Dialog.Header>

        <div className='space-y-4 py-2'>
          <div className='rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-fg'>
            <Trans>
              Selected items: {documentRows.length} documents,{' '}
              {folderIds.length} folders.
            </Trans>
          </div>

          {skippedManualDocumentsCount > 0 && (
            <div className='rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-fg'>
              <Trans>
                Manual documents in the selection will be skipped (
                {skippedManualDocumentsCount}).
              </Trans>
            </div>
          )}

          {hasWebsiteManagedFolder && (
            <div className='rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-fg'>
              <Trans>
                Website-only settings apply to tracked website folders and are
                ignored for other selected items.
              </Trans>
            </div>
          )}

          <AutoUpdateSettingsForm
            mode='bulk'
            showChangeThreshold={isSuperAdmin}
            showDiscoveryFrequency={hasWebsiteRootFolder}
            showWebsiteFields={hasWebsiteManagedFolder}
            showAutoIncludeRootScanHint={
              hasWebsiteManagedFolder && !hasWebsiteRootFolder
            }
            updateFrequency={updateFrequency}
            discoveryFrequency={discoveryFrequency}
            changeThreshold={changeThreshold}
            autoIncludeNewPages={autoIncludeNewPages}
            removalBehavior={removalBehavior}
            onUpdateFrequencyChange={setUpdateFrequency}
            onDiscoveryFrequencyChange={setDiscoveryFrequency}
            onChangeThresholdChange={setChangeThreshold}
            onAutoIncludeNewPagesChange={setAutoIncludeNewPages}
            onRemovalBehaviorChange={setRemovalBehavior}
          />
        </div>

        <Dialog.Footer>
          <Button variant='ghost' outline onClick={() => onOpenChange(false)}>
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={handleSave} loading={isSaving}>
            <Trans>Save</Trans>
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
});
